import {Inject, Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {APP_CONFIG, AppConfig} from '@src/app.config';
import {RequestService} from '@src/app/shared/services/http/request/request.service';
import {AppointmentDetail} from "@app/areas/appointments/models/appointment.interface";
import {AppointmentType} from "@app/areas/appointments/models/appointment-type.enum";

export interface CreateAppointmentRequest {
  title: string;
  startTime: string;
  endTime: string;
  appointmentType: AppointmentType;
  targetUserId: string;
}

export interface CreateParticipantRequest {
  userId: string;
  title: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  profession: string | null;
  profilePicture: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AppointmentApiService {
  constructor(
    private requestService: RequestService,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
  ) {
  }

  get(id: string): Observable<AppointmentDetail> {
    const url = `${this.appConfig.angularEndpoint}/appointments/detail/${id}`;
    return this.requestService.get(url).pipe(map(r => r.body))
  }

  getForUser(userId: string, startTime?: string): Observable<AppointmentDetail[]> {
    const url = startTime
      ? `${this.appConfig.angularEndpoint}/appointments/forUser/${encodeURIComponent(userId)}?startTime=${encodeURIComponent(startTime)}`
      : `${this.appConfig.angularEndpoint}/appointments/forUser/${encodeURIComponent(userId)}`;

    return this.requestService.get(url).pipe(map(r => r.body ?? []));
  }

  /**
   * Updates the participant entity for the logged in user
   * @param firstName
   * @param lastName
   */
  updateParticipant(firstName: string, lastName: string): Observable<any> {
    const url = `${this.appConfig.angularEndpoint}/appointments/participant`;
    return this.requestService.put(url, {firstName, lastName}).pipe(map(r => r.body))
  }

  getParticipant(): Observable<any> {
    const url = `${this.appConfig.angularEndpoint}/appointments/participant`;
    return this.requestService.get(url).pipe(map(r => r.body))
  }

  list(startTime: string, endTime: string): Observable<any[]> {
    const url = `${this.appConfig.angularEndpoint}/appointments?startTime=${startTime}&endTime=${endTime}`;
    return this.requestService.get(url).pipe(map(r => r.body))
  }

  joinRoom(appointmentId: string): Observable<{ token: string, roomName: string }> {
    const url = `${this.appConfig.angularEndpoint}/appointments/joinMeeting/${appointmentId}`;
    return this.requestService.post(url).pipe(map(r => r.body))
  }

  inviteViaPhone(appointmentId: string, phoneNumber: string): Observable<any> {
    const url = `${this.appConfig.angularEndpoint}/appointments/inviteFromPhone/${appointmentId}`;
    return this.requestService.post(url, {phoneNumber}).pipe(map(r => r.body))
  }

  accept(appointmentId: string): Observable<AppointmentDetail> {
    const url = `${this.appConfig.angularEndpoint}/appointments/accept/${appointmentId}`;
    return this.requestService.post(url).pipe(map(r => r.body))
  }

  reject(appointmentId: string): Observable<AppointmentDetail> {
    const url = `${this.appConfig.angularEndpoint}/appointments/reject/${appointmentId}`;
    return this.requestService.post(url).pipe(map(r => r.body))
  }

  cancel(appointmentId: string): Observable<AppointmentDetail> {
    const url = `${this.appConfig.angularEndpoint}/appointments/cancel/${appointmentId}`;
    return this.requestService.delete(url).pipe(map(r => r.body))
  }

  proposeNewTime(appointmentId: string, startTime: string, endTime: string): Observable<AppointmentDetail> {
    const url = `${this.appConfig.angularEndpoint}/appointments/proposeNewTime/${appointmentId}`;
    return this.requestService.post(url, {startTime, endTime}).pipe(map(r => r.body))
  }

  createAppointment(payload: CreateAppointmentRequest): Observable<AppointmentDetail> {
    const url = `${this.appConfig.angularEndpoint}/appointments`;
    return this.requestService.post(url, payload).pipe(map(r => r.body))
  }

  addParticipant(appointmentId: string, participant: CreateParticipantRequest): Observable<AppointmentDetail> {
    const url = `${this.appConfig.angularEndpoint}/appointments/${appointmentId}/add-participant`;
    return this.requestService.post(url, participant).pipe(map(r => r.body))
  }
}
